import { useEffect, useState } from "react";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { defaultUrl } from "../features/actions";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { GrDocumentPdf } from "react-icons/gr";
import { makeFetchRequest } from "../utilities/common";
import { FaClock, FaCloudDownloadAlt, FaInbox } from "react-icons/fa";

interface FeedType {
  id: string;
  description: string;
  activity_type: string;
  data: any;
  doc: string;
  image: string[];
  video: {
    props: {
      src: string;
    };
  };
  activity_time: string;
}

export const StaffStudentFeed = () => {
  const [activities, setActivities] = useState<FeedType[]>([]);
  let { pupil_id } = useParams();

  useEffect(() => {
    fetchStudentActivities(pupil_id as string);
  }, []);

  async function fetchStudentActivities(pupil_id: string) {
    try {
      toast.remove();
      toast.loading("Fetching student activity...");
      const result = await makeFetchRequest(
        `${defaultUrl}/activity_feed?student_id=${pupil_id}&per_page=100`,
      );

      let feed: any[] = result;
      feed.forEach((item, i) => {
        if (item?.activity_type === "report-card") {
          delete item["data"];
          item["image"] = null;
          item["video"] = null;
        }
        if (item.data) {
          item.data = Object.entries(item.data);
        }
        if (item.image && item.image[0] !== "null") {
          item.image = (
            <img
              src={item.image}
              alt="activity"
              className="rounded-xl border border-gray-100 max-w-full sm:max-w-[400px] h-auto shadow-xs mt-2"
            />
          );
        } else {
          item.image = null;
        }
        if (item.video !== "null") {
          item.video = (
            <video
              src={item.video}
              className="rounded-xl border border-gray-100 max-w-full sm:max-w-[400px] mt-2"
            />
          );
        } else {
          item.video = null;
        }

        const date = new Date(item.activity_time);
        const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${date
          .getDate()
          .toString()
          .padStart(2, "0")} ${date
          .getHours()
          .toString()
          .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
        item.activity_time = formattedDate;
      });
      setActivities(result);
      toast.remove();
      toast.success("Fetch completed");
    } catch (err: any) {
      toast.remove();
      toast.error("Fetch unsuccessful");
    }
  }

  // Refactored Factory to generate perfect premium badges and borders matching Pendulum standards
  function colorFactory(id: string) {
    const base = id.toLowerCase();
    if (base === "login")
      return {
        color: "#1768A9",
        border: "border-l-[5px] border-l-[#1768A9]",
        bg: "bg-[#CEE9FF]/40",
      };
    if (base === "health")
      return {
        color: "#5E9F0D",
        border: "border-l-[5px] border-l-[#5E9F0D]",
        bg: "bg-[#F3FCE8]/50",
      };
    if (base === "temperature")
      return {
        color: "#227DB2",
        border: "border-l-[5px] border-l-[#227DB2]",
        bg: "bg-[#E8F6FF]",
      };
    if (base === "food")
      return {
        color: "#D3311E",
        border: "border-l-[5px] border-l-[#D3311E]",
        bg: "bg-[#FCD9D5]/40",
      };
    if (base === "liquids")
      return {
        color: "#F127A8",
        border: "border-l-[5px] border-l-[#F127A8]",
        bg: "bg-[#F8EAF3]",
      };
    if (base === "mood")
      return {
        color: "#29376D",
        border: "border-l-[5px] border-l-[#29376D]",
        bg: "bg-[#DBECFD]/50",
      };
    if (base === "sleeping time")
      return {
        color: "#234CA8",
        border: "border-l-[5px] border-l-[#234CA8]",
        bg: "bg-[#EEEAF8]",
      };
    if (base === "toilet")
      return {
        color: "#025166",
        border: "border-l-[5px] border-l-[#025166]",
        bg: "bg-[#E7FAFF]",
      };
    if (base === "note")
      return {
        color: "#CF235A",
        border: "border-l-[5px] border-l-[#CF235A]",
        bg: "bg-[#FFD6E3]/40",
      };
    if (base === "sport")
      return {
        color: "#195AC7",
        border: "border-l-[5px] border-l-[#195AC7]",
        bg: "bg-[#F3F9FF]",
      };
    if (base === "supplies")
      return {
        color: "#195AC7",
        border: "border-l-[5px] border-l-[#195AC7]",
        bg: "bg-[#E3E9F3]",
      };
    if (base === "logout")
      return {
        color: "#583F05",
        border: "border-l-[5px] border-l-[#583F05]",
        bg: "bg-[#FEECC1]/40",
      };
    if (base === "school activity")
      return {
        color: "#234CA8",
        border: "border-l-[5px] border-l-[#234CA8]",
        bg: "bg-[#EEEAF8]",
      };

    return {
      color: "#012063",
      border: "border-l-[5px] border-l-[#012063]",
      bg: "bg-slate-100",
    };
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 flex flex-col space-y-6 text-left">
      {/* Upper Navigation Row Header */}
      <div className="w-full flex items-center">
        <BackButton />
        <TopAdminNav name="Jone Doe" title="Pupil Activity Timeline" />
      </div>
      {/* Main Stream Canvas Box */}
      <section className="flex-1 overflow-y-auto max-h-[80vh] pr-1 register-scroll pb-12">
        <div className="max-w-4xl mx-auto space-y-4">
          {activities?.length ? (
            activities.map((item, index) => {
              const theme = colorFactory(item.activity_type);
              return (
                <div
                  key={index}
                  className={`bg-white border border-gray-100 rounded-2xl p-5 md:p-6 shadow-xs flex flex-col justify-between transition-all duration-200 hover:shadow-md ${theme.border}`}
                >
                  <div className="space-y-4">
                    {/* Header Row: Badge Identifier Allocation */}
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span
                        style={{ color: theme.color }}
                        className={`inline-flex items-center px-3 py-1 rounded-xl text-xs font-black tracking-wider uppercase ${theme.bg}`}
                      >
                        {item.activity_type}
                      </span>
                    </div>

                    {/* Metadata Render Body Matrix */}
                    <div className="space-y-2 text-[14px] text-slate-600 font-medium">
                      {item.data?.map((entry: any[], idx: number) => {
                        if (entry[0] === "items_needed") {
                          return (
                            <div
                              key={idx}
                              className="flex flex-wrap items-baseline gap-1 py-0.5"
                            >
                              <span className="capitalize text-xs font-bold text-slate-400 w-32 shrink-0">
                                Items Needed:
                              </span>
                              <div className="text-[#012063] font-bold">
                                {Array.isArray(entry[1])
                                  ? entry[1].map(
                                      (subItem: string, i: number) => (
                                        <span
                                          key={i}
                                        >{`${subItem}${i === entry[1].length - 1 ? "" : ", "}`}</span>
                                      ),
                                    )
                                  : entry[1]}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={idx}
                            className="flex flex-wrap items-baseline gap-1 py-0.5"
                          >
                            <span className="capitalize text-xs font-bold text-slate-400 w-32 shrink-0">
                              {entry[0].replace(/_/g, " ")}:
                            </span>
                            <span className="text-[#012063] font-bold truncate max-w-md">
                              {entry[1]}
                            </span>
                          </div>
                        );
                      })}

                      {/* Explicit Description Block */}
                      {item.description && (
                        <div className="pt-2 border-t border-gray-50/50 mt-2">
                          <span className="text-xs font-bold text-slate-400 block mb-1">
                            Description Notes:
                          </span>
                          <p className="text-[14px] text-slate-700 font-medium leading-relaxed bg-slate-50 rounded-xl p-3 border border-gray-100/70 max-h-24 overflow-y-auto register-scroll">
                            {item.description}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Conditional Media Feed Assets Layout Layers */}
                    {item.image && (
                      <div className="pt-2 overflow-hidden">{item.image}</div>
                    )}

                    {item.video && item?.video?.props?.src && (
                      <div className="pt-2 overflow-hidden rounded-xl border border-gray-100 shadow-xs bg-black max-w-full sm:max-w-[500px]">
                        <video controls className="w-full h-auto max-h-[300px]">
                          <source
                            src={item?.video?.props?.src}
                            type="video/mp4"
                          />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    )}

                    {item.doc && (
                      <div className="pt-2">
                        <a
                          href={item.doc}
                          download
                          className="inline-flex items-center gap-3 px-4 py-3 bg-rose-50 hover:bg-rose-100 border border-rose-100 rounded-xl text-rose-700 text-xs font-bold transition-all"
                        >
                          <GrDocumentPdf className="text-2xl text-rose-600 shrink-0" />
                          <div className="text-left">
                            <p className="font-black leading-none">
                              Attached Asset Document
                            </p>
                            <p className="text-[10px] text-rose-500 font-medium mt-1 inline-flex items-center gap-1">
                              <FaCloudDownloadAlt /> Click to securely save PDF
                              file
                            </p>
                          </div>
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Absolute Bottom Timestamp Indicator Tracker */}
                  <div className="w-full flex items-center justify-end text-[11px] text-slate-400 font-bold font-mono tracking-wide mt-4 pt-2 border-t border-gray-50/60">
                    <span className="mr-1.5 text-xs text-[#0096D1]">
                      <FaClock />
                    </span>
                    {item.activity_time}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-white border border-dashed border-gray-200 rounded-2xl h-52 w-full flex flex-col items-center justify-center text-slate-400 gap-3">
              <FaInbox className="text-3xl text-slate-300" />
              <p className="text-xs font-bold tracking-tight italic">
                No activities logged for this pupil repository yet...
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
